/**
 * „Heute" — die Einstiegsseite in der Form des Neuentwurfs.
 *
 * Der Entwurf baut sie von oben nach unten aus vier Teilen: Datumszeile in
 * Mono, Titel in 54px Gewicht 200, ein Kennzahlband über einer Tintenlinie und
 * darunter zwei Spalten — links die Listen, rechts der Metarr-Vorschlag und die
 * Projekte.
 *
 * ── Warum links zwei Listen stehen ────────────────────────────────────────
 *
 * Ursprünglich stand links nur „Zuletzt bewegt". Auf einer Seite namens
 * „Heute" beantwortete das die falsche Frage: Die einzige Liste war ein
 * Rückblick, und weil Erledigtes nicht ausgeschlossen war, füllte sie sich mit
 * dem, was vorbei ist — am Montagmorgen sah man, was Freitag fertig wurde.
 * Hieße die Seite „Puls", hätte am Inhalt nichts geändert werden müssen.
 *
 * Deshalb steht **„Heute fällig"** jetzt oben: Überfälliges und heute
 * Fälliges, das Älteste zuerst. Das ist die einzige Liste mit einem Auftrag.
 * „Zuletzt bewegt" bleibt darunter als das, was es immer war — ein Rückblick,
 * und mit einem Zähler, der aus derselben Menge zieht wie die Liste selbst.
 *
 * Keine Karten mehr. Vorher standen hier zwei `Card`-Flächen mit Kopf, Liste
 * und Fuß; der Entwurf legt dieselben Inhalte auf das Papier und trennt sie mit
 * Rubrikenlinien. Damit fällt auch der Kartenfuß weg — der Verweis „Alle
 * Aufgaben" sitzt jetzt in der Rubrik, wo er gelesen wird, statt am Ende einer
 * Fläche.
 *
 * ── Woher die Zahlen kommen ───────────────────────────────────────────────
 *
 * Die vorige Fassung hielt hier fest: *„Offen ist keine Eigenschaft der Zeile —
 * es hängt am Zustand, und der gilt je Projekt."* Das stimmt, und deshalb lädt
 * diese Seite die Zustände mit: `zustaende.gruppe` führt `offen`, `in_arbeit`,
 * `abnahme`, `erledigt` und `verworfen`, und genau darauf sind die Nebenwerte
 * des Entwurfs abgebildet. Gerechnet wird nichts geschätzt.
 *
 * Fristen laufen über `gruppeVon` und `formuliereFrist` aus
 * `@cubic/shared` — dieselbe Rechnung wie in der Aufgabenliste, in der
 * Zeitzone der Person und nicht in der des Servers.
 */

import { Link } from "@tanstack/react-router";
import { gruppeVon, formuliereBewegt, formuliereFrist } from "@cubic/shared";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Titel } from "@/components/entwurf/Titel";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Kennzahl } from "@/components/entwurf/Kennzahl";
import { MetarrKarte } from "@/components/entwurf/MetarrKarte";
import { OffeneMrBand } from "@/components/entwurf/OffeneMrBand";
import { Zahlenband, Nebenwert } from "@/components/entwurf/Zahlenband";
import { Rubrik } from "@/components/entwurf/Rubrik";
import {
  Kennung,
  Zeilentitel,
  zeileKlassen,
} from "@/components/entwurf/Zeile";
import { Zustandspunkt } from "@/components/entwurf/Zustandspunkt";
import { verweisKlassen } from "@/components/entwurf/Verweis";
import {
  ZUSTAND,
  istAbgeschlossen,
  zustandAusGruppe,
} from "@/lib/entwurf-zustaende";
import {
  fehlerMeldung,
  useAufgabenFuerProjekte,
  useIch,
  usePerson,
  useProjekte,
  useZustaendeFuerProjekte,
} from "@/lib/haken";
import { leererFilter } from "@/lib/filter";
import { darfAktion } from "@/lib/rollen";
import { cx } from "@/lib/utils";
import { ALLE_PROJEKTE, useProjektAuswahl } from "@/lib/projekt-auswahl";

/** Was eine Aufgabenzeile auf dieser Seite zeigt. */
type Aufgabenzeile = {
  id: string;
  titel: string;
  nummer: number;
  projektId: string;
  zustandsname: string;
  punkt: (typeof ZUSTAND)[keyof typeof ZUSTAND];
  fristgruppe: string;
  frist: string;
  /** Wann zuletzt bewegt — knapp, für die Spalte. */
  bewegt: string;
  /** Derselbe Zeitpunkt vollständig, für den Titel beim Überfahren. */
  bewegtGenau: string;
};

/**
 * Eine Zeile — einmal geschrieben, von beiden Listen benutzt.
 *
 * Das Zustandswort steht auf schmalen Schirmen nur für Vorleseprogramme da.
 * Gemessen bei 375px: Der Titel bekam 104px, das Zustandswort 63px — das
 * Element, das sagt **was** zu tun ist, verlor gegen das, das sagt **wo** es
 * steht. Der Punkt bleibt sichtbar und trägt die Farbe; das Wort kommt ab
 * `sm` zurück.
 *
 * ── Die rechte Spalte zeigt, was die Liste fragt ──────────────────────────
 *
 * Beide Listen benutzen dieselbe Zeile, aber sie stellen nicht dieselbe Frage.
 * „Heute fällig" fragt *wann ist es fällig*, „Zuletzt bewegt" fragt *wann ist
 * etwas passiert*. Bis hierher zeigten beide die Frist — und weil ein
 * Rückblick sich nicht nach Fälligkeit sortiert, stand die Spalte im Rückblick
 * an allen sechs Zeilen **leer**: 62px reserviert, nichts darin, während der
 * Titel daneben bei vierzig Zeichen abschnitt. Der Sortierschlüssel der Liste
 * war damit nirgends zu sehen; „heute bewegt" und „im Mai bewegt" sahen gleich
 * aus.
 */
function AufgabenZeile({
  aufgabe,
  kuerzel,
  zeitspalte = "frist",
}: {
  aufgabe: Aufgabenzeile;
  kuerzel: string | null | undefined;
  zeitspalte?: "frist" | "bewegt";
}) {
  /*
   * Der Akzent gehört der Frist, nicht dem Zeitpunkt.
   *
   * Er bedeutet auf dieser Seite „das verlangt eine Entscheidung" — die
   * Leitzahl trägt ihn aus demselben Grund. Ein Änderungszeitpunkt verlangt
   * nichts, auch wenn die Aufgabe daneben überfällig ist; das sagt in dieser
   * Liste der Zustandspunkt.
   */
  const ueberfaellig =
    zeitspalte === "frist" && aufgabe.fristgruppe === "ueberfaellig";
  return (
    <li>
      {/*
        Die ganze Zeile ist der Verweis — ein echtes `Link` und kein `div`
        mit `onClick`. Damit trägt sie Tastaturfokus, Mittelklick und
        Kontextmenü.
      */}
      <Link
        to="/aufgaben/$id"
        params={{ id: aufgabe.id }}
        className={zeileKlassen({ klickbar: true })}
      >
        <Kennung>
          {kuerzel ?? "—"}-{aufgabe.nummer}
        </Kennung>
        <Zeilentitel titel={aufgabe.titel}>{aufgabe.titel}</Zeilentitel>
        <span className="flex flex-none items-center gap-0 sm:gap-2">
          <Zustandspunkt bild={aufgabe.punkt} />
          <Mikrolabel ton="mittel" className="sr-only sm:not-sr-only">
            {aufgabe.zustandsname}
          </Mikrolabel>
        </span>
        {/*
          Feste Breite und rechts ausgerichtet, damit die Zeitangaben eine
          Spalte bilden. Überfälliges nimmt den Akzent — derselbe Ton wie die
          Leitzahl oben, und er bedeutet hier dasselbe.

          70px statt 62px, und `whitespace-nowrap` dazu: Die längsten Texte
          sind „vor 59 min" und „seit 100 T", beide zehn Zeichen. In 62px
          passten nur neun, und weil an einem Span nichts das Umbrechen
          verbietet, wäre der zehnte in eine zweite Zeile gerutscht und hätte
          die 52px-Zeile aufgesprengt.
        */}
        <Mikrolabel
          zahl
          betont={ueberfaellig}
          title={
            zeitspalte === "bewegt" ? aufgabe.bewegtGenau || undefined : undefined
          }
          className={cx(
            "w-[70px] flex-none whitespace-nowrap text-right",
            ueberfaellig && "text-akzent",
          )}
        >
          {zeitspalte === "bewegt" ? aufgabe.bewegt : aufgabe.frist}
        </Mikrolabel>
      </Link>
    </li>
  );
}

export function Uebersicht() {
  const ich = useIch();
  const person = usePerson(ich.data?.personId);
  const projekte = useProjekte();
  const { auswahl } = useProjektAuswahl();
  const alleProjekte = projekte.data ?? [];
  const liste =
    auswahl === ALLE_PROJEKTE
      ? alleProjekte
      : alleProjekte.filter((projekt) => projekt.id === auswahl);
  const darfAnlegen = darfAktion({
    aktion: "projekt_anlegen",
    rechte: ich.data?.rechte,
  });

  const projektIds = liste.map((p) => p.id);
  const aufgaben = useAufgabenFuerProjekte(projektIds, leererFilter());
  const zustaende = useZustaendeFuerProjekte(projektIds);

  // Ohne Zeitzone der Person nicht rechnen, sondern die Vorgabe nehmen — sonst
  // springt „heute fällig" beim Nachladen einmal um.
  const zeitzone = person.data?.zeitzone ?? "Europe/Berlin";

  const gruppeZu = new Map(
    (zustaende.data ?? []).map((z) => [z.id, { name: z.name, gruppe: z.gruppe }]),
  );
  const kuerzelZu = new Map(liste.map((p) => [p.id, p.kuerzel]));

  const offeneAufgaben = (aufgaben.data ?? []).filter(
    (a) => a.archiviertAm === null,
  );

  /*
   * Der vollständige Zeitpunkt für den Titel beim Überfahren.
   *
   * Einmal gebaut und nicht je Zeile: Diese Abbildung läuft über **alle**
   * offenen Aufgaben, nicht über die sechs sichtbaren, und ein
   * `Intl.DateTimeFormat` je Durchlauf fällt bei dreistelligen Zahlen auf.
   *
   * Er ist die genaue Auskunft hinter der knappen: „vor 3 h" sagt, worauf es
   * ankommt, „15. Juni 2026, 09:12" beantwortet die Rückfrage — und löst
   * nebenbei den einen Fall auf, in dem die knappe Fassung mehrdeutig wird:
   * ein „23.05." aus dem Vorjahr.
   */
  const zeitpunktFormat = new Intl.DateTimeFormat("de-DE", {
    timeZone: zeitzone,
    dateStyle: "medium",
    timeStyle: "short",
  });
  const genauerZeitpunkt = (wert: string): string => {
    const zeitpunkt = new Date(wert);
    return Number.isNaN(zeitpunkt.getTime())
      ? ""
      : zeitpunktFormat.format(zeitpunkt);
  };

  /** Eine Zeile mit allem, was der Entwurf an ihr zeigt. */
  const zeilen = offeneAufgaben.map((a) => {
    const zustand = a.zustandId ? gruppeZu.get(a.zustandId) : undefined;
    const gruppe = zustand?.gruppe ?? "offen";
    const erledigt = istAbgeschlossen(gruppe);
    const fristgruppe = gruppeVon({ faelligAm: a.faelligAm, erledigt }, zeitzone);
    return {
      ...a,
      zustandsname: zustand?.name ?? "Offen",
      punkt: ZUSTAND[zustandAusGruppe(gruppe)],
      gruppe,
      erledigt,
      fristgruppe,
      frist: formuliereFrist({ faelligAm: a.faelligAm, erledigt }, fristgruppe, zeitzone),
      bewegt: formuliereBewegt(a.geaendertAm, zeitzone),
      bewegtGenau: genauerZeitpunkt(a.geaendertAm),
    };
  });

  /*
   * Ob die Zahlen überhaupt gerechnet werden konnten.
   *
   * Der Kopfkommentar dieser Seite verspricht „gerechnet wird nichts
   * geschätzt". Genau das brach das Zahlenband: Es rendert, sobald die
   * Projekte da sind, und zeigte fünf Nullen, während die Aufgaben noch
   * unterwegs waren — direkt über einer Liste, die korrekt „Wird geladen …"
   * sagte. Im Fehlerfall blieben die Nullen dauerhaft stehen.
   *
   * Die Nullregel machte es schlimmer statt besser: Die falsche Null tritt
   * leise zurück und wird darum geglaubt. Ohne Zustände zählen `in Arbeit`
   * und `in Abnahme` ebenfalls falsch, deshalb hängen sie mit drin.
   */
  const zahlenBekannt =
    !aufgaben.isLoading &&
    !aufgaben.isError &&
    !zustaende.isLoading &&
    !zustaende.isError;

  /** Was statt einer Zahl steht, solange keine da ist. */
  const zahl = (wert: number): number | string => (zahlenBekannt ? wert : "—");

  /** Die Zustands-IDs einer Gruppe, über alle sichtbaren Projekte hinweg. */
  const idsDerGruppe = (gesucht: string): string[] =>
    (zustaende.data ?? [])
      .filter((z) => z.gruppe === gesucht)
      .map((z) => z.id);

  /**
   * Das Ziel einer Kennzahl in der Aufgabenliste — oder `undefined`.
   *
   * `undefined` heißt: kein Verweis. Das ist wichtiger als es klingt. Eine
   * Zahl, die noch „—" ist, darf nirgends hinführen, und ein Zustandsfilter
   * ohne bekannte IDs führte auf die **ungefilterte** Liste — also auf eine
   * andere Menge, als die Zahl behauptet. Lieber kein Verweis als ein
   * falscher.
   */
  const zuAufgaben = (
    was: { frist?: string; zustand?: string[] },
  ): Record<string, string> | undefined => {
    if (!zahlenBekannt) return undefined;
    if (was.zustand && was.zustand.length === 0) return undefined;
    const suche: Record<string, string> = {};
    // Ist ein Projekt gewählt, zeigt der Verweis denselben Ausschnitt.
    if (auswahl !== ALLE_PROJEKTE) suche["projekt"] = auswahl;
    if (was.frist) suche["frist"] = was.frist;
    if (was.zustand?.length) suche["zustand"] = was.zustand.join(",");
    return suche;
  };

  const nichtAbgeschlossen = zeilen.filter((z) => !z.erledigt);
  const ueberfaellig = nichtAbgeschlossen.filter(
    (z) => z.fristgruppe === "ueberfaellig",
  ).length;
  const heute = nichtAbgeschlossen.filter((z) => z.fristgruppe === "heute").length;
  const inArbeit = zeilen.filter((z) => z.gruppe === "in_arbeit").length;
  const inAbnahme = zeilen.filter((z) => z.gruppe === "abnahme").length;
  const offen = nichtAbgeschlossen.length;

  /*
   * Was heute ansteht — die Liste, die den Namen der Seite einlöst.
   *
   * Vorher stand hier nur „Zuletzt bewegt", also ein Rückblick. Auf einer
   * Seite namens „Heute" beantwortete das die falsche Frage: Am Montagmorgen
   * sah man, was Freitag fertig wurde. Überfälliges zuerst, dann das heute
   * Fällige, beides nach Datum — die älteste Schuld oben.
   */
  const heuteFaellig = nichtAbgeschlossen
    .filter(
      (z) => z.fristgruppe === "ueberfaellig" || z.fristgruppe === "heute",
    )
    .sort((a, b) => {
      if (a.fristgruppe !== b.fristgruppe) {
        return a.fristgruppe === "ueberfaellig" ? -1 : 1;
      }
      return (a.faelligAm ?? "").localeCompare(b.faelligAm ?? "");
    });

  const zuletzt = [...zeilen].sort((a, b) =>
    b.geaendertAm.localeCompare(a.geaendertAm),
  );

  /*
   * Die Projekte in einer Reihenfolge, die man begründen kann.
   *
   * Vorher war die Liste auf sechs geschnitten und kam in der Reihenfolge der
   * API — es waren immer dieselben sechs, und nichts sagte, welche. Jetzt
   * stehen die mit der meisten offenen Arbeit oben; die Zahl daneben macht
   * die Regel selbsterklärend. Bei Gleichstand entscheidet der Name, damit
   * die Reihenfolge zwischen zwei Aufrufen nicht springt.
   */
  const projekteGeordnet = liste
    .map((p) => ({
      ...p,
      offen: zeilen.filter((z) => z.projektId === p.id && !z.erledigt).length,
    }))
    .sort(
      (a, b) => b.offen - a.offen || a.name.localeCompare(b.name, "de"),
    );

  const datum = new Intl.DateTimeFormat("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: zeitzone,
  }).format(new Date());

  if (projekte.isLoading) {
    return <Mikrolabel grad={12}>Lade Übersicht …</Mikrolabel>;
  }

  /*
   * Ein gescheiterter Abruf ist kein leeres Konto.
   *
   * Vorher wurde `isError` nirgends geprüft. Nach dem Wiederholungsversuch war
   * `isLoading` falsch und `data` leer — `liste.length === 0` griff, und die
   * Seite begrüßte eine Person mit zwölf Projekten mit „Willkommen. Lege das
   * erste Projekt an." Sie empfahl damit die falsche Handlung, und zwar
   * genau dann, wenn ohnehin etwas kaputt war.
   *
   * Der Kopf bleibt stehen: Datum und Titel sind auch dann wahr, wenn der
   * Server schweigt.
   */
  if (projekte.isError) {
    return (
      <div className="max-w-[1160px]">
        <Mikrolabel grad={12}>{datum}</Mikrolabel>
        <Titel grad={54} className="mt-3.5">
          Heute
        </Titel>
        <div className="mt-13">
          <Fehlerzeile
            meldung={fehlerMeldung(projekte.error)}
            onErneut={() => void projekte.refetch()}
          />
        </div>
      </div>
    );
  }

  if (liste.length === 0) {
    return (
      <div className="max-w-[1160px]">
        <Mikrolabel grad={12}>{datum}</Mikrolabel>
        <Titel grad={54} className="mt-3.5">
          Heute
        </Titel>
        {/*
          Kein `Leerzustand` mehr an dieser Stelle.

          Der bringt eine eigene Formsprache mit — zentriert, graue Fläche,
          Avatar — und stand damit als einziges Kastenelement in einer Seite,
          die sonst nur aus Linien besteht. Hier steht stattdessen dieselbe
          Grammatik wie überall: eine Tintenlinie, ein Satz, ein Verweis.

          Und der Satz richtet sich nach dem, was möglich ist. Wer nicht
          anlegen darf, bekam vorher trotzdem „Lege das erste Projekt an" zu
          lesen — eine Aufforderung ohne Ausführbarkeit.
        */}
        <div className="mt-13 border-t border-tinte pt-[22px]">
          <p className="max-w-[52ch] text-[15px] font-light leading-[1.7] text-tinte2">
            {darfAnlegen
              ? "Noch kein Projekt. Sobald eines besteht, stehen hier die fälligen Aufgaben, der Verlauf und die Vorschläge von Metarr."
              : "Noch kein Projekt für dich freigegeben. Sobald dich jemand einem Projekt zuordnet, steht hier deine Arbeit."}
          </p>
          {darfAnlegen ? (
            <div className="mt-5">
              {/*
                Ein echter Verweis statt `location.assign`. Der lud in einer
                Einzelseitenanwendung die ganze Anwendung neu — genau die
                Bauform, die `Zeile.tsx` für Zeilen ausdrücklich verwirft.
              */}
              <Link to="/projekte" className={verweisKlassen({ grad: 14 })}>
                Projekt anlegen
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1160px]">
      <div>
        <Mikrolabel grad={12}>{datum}</Mikrolabel>
        <Titel grad={54} className="mt-3.5">
          Heute
        </Titel>
      </div>

      {/*
        Die Leitzahl ist das Überfällige — die einzige Zahl auf dieser Seite,
        die eine Entscheidung verlangt, und deshalb die einzige im Akzent. Die
        Nullregel greift über `ton`: Sind keine überfällig, tritt die Null
        zurück, statt in Akzentfarbe Alarm zu behaupten.
      */}
      <Zahlenband
        className="mt-13"
        neben={
          <>
            <Nebenwert
              wert={zahl(heute)}
              label="heute fällig"
              zu={zuAufgaben({ frist: "heute" })}
            />
            <Nebenwert
              wert={zahl(inArbeit)}
              label="in Arbeit"
              zu={zuAufgaben({ zustand: idsDerGruppe("in_arbeit") })}
            />
            <Nebenwert
              wert={zahl(inAbnahme)}
              label="in Abnahme"
              zu={zuAufgaben({ zustand: idsDerGruppe("abnahme") })}
            />
            <Nebenwert wert={zahl(offen)} label="offen" zu={zuAufgaben({})} />
          </>
        }
      >
        {/*
          Die Leitzahl führt jetzt dorthin, wo sie hinzeigt. Vorher benannte
          das Band die Arbeit und bot keinen Weg zu ihr — fünf Zahlen, null
          Klickziele, obwohl `/aufgaben?frist=ueberfaellig` längst eine
          gültige, teilbare Adresse ist.
        */}
        <Kennzahl
          label="Überfällig"
          wert={zahl(ueberfaellig)}
          ton="akzent"
          zu={zuAufgaben({ frist: "ueberfaellig" })}
        />
      </Zahlenband>

      {/*
        Ein Satz, der eine Frage beantwortet, die sonst jeder für sich stellt:
        Darf ich das addieren? Nein — alle vier Nebenwerte sind Teilmengen von
        „offen", und „heute fällig" überschneidet sich zusätzlich mit „in
        Arbeit". Ohne diesen Hinweis liest sich das Band wie eine Aufteilung,
        und die Summe wäre erfunden.

        Bewusst leise und nur, wenn tatsächlich Zahlen dastehen.
      */}
      {zahlenBekannt ? (
        <Mikrolabel grad={11} className="mt-3.5 block">
          Alle Werte neben der Leitzahl sind Teilmengen von „offen" und können
          sich überschneiden.
        </Mikrolabel>
      ) : null}

      {/*
        Über den Listen, nicht darunter: Ein offener Merge Request wartet auf
        eine Person und gehört damit zu dem, was heute ansteht — nicht in den
        Rückblick. Ohne offene Merge Requests fehlt das Band ganz.
      */}
      <OffeneMrBand className="mt-13" />

      <div className="mt-16 flex flex-wrap items-start gap-x-12 gap-y-14">
        <div className="min-w-0 flex-1 basis-[560px]">
          {/*
            Die Liste, die den Namen der Seite einlöst. Sie steht zuerst,
            weil sie als einzige einen Auftrag enthält; „Zuletzt bewegt"
            darunter ist der Rückblick und war vorher das Einzige, was es
            gab.
          */}
          <Rubrik
            id="rubrik-heute"
            anzahl={
              zahlenBekannt && heuteFaellig.length > 6
                ? `6 von ${heuteFaellig.length}`
                : undefined
            }
            aktion={
              /*
                Ohne `frist`-Filter, aber in der Standardsortierung — und die
                ist „frist", also Überfälliges zuerst. Ein Verweis auf
                `frist=heute` ließe genau die Hälfte weg, die drängt.
              */
              <Link
                to="/aufgaben"
                search={zuAufgaben({}) ?? {}}
                className={verweisKlassen()}
              >
                Alle nach Frist
              </Link>
            }
          >
            Heute fällig
          </Rubrik>

          {aufgaben.isError ? (
            <div className="pt-4">
              <Fehlerzeile
                meldung={fehlerMeldung(aufgaben.error)}
                onErneut={() => void aufgaben.refetch()}
              />
            </div>
          ) : !zahlenBekannt ? (
            <div className="pt-4">
              <Mikrolabel>Wird geladen …</Mikrolabel>
            </div>
          ) : heuteFaellig.length === 0 ? (
            /*
              Kein Alarm und keine Datenauskunft, sondern eine Entlastung.
              Die Nullregel gilt auch für Sätze: Wenn nichts fällig ist, ist
              das kein Ereignis, sondern eine gute Nachricht.
            */
            <div className="pt-4">
              <Mikrolabel>Nichts fällig heute, nichts überfällig.</Mikrolabel>
            </div>
          ) : (
            <ul role="list" aria-labelledby="rubrik-heute">
              {heuteFaellig.slice(0, 6).map((a) => (
                <AufgabenZeile
                  key={a.id}
                  aufgabe={a}
                  kuerzel={kuerzelZu.get(a.projektId)}
                />
              ))}
            </ul>
          )}

          <Rubrik
            className="mt-11"
            id="rubrik-zuletzt"
            /*
              Zähler und Liste ziehen jetzt aus derselben Menge. Vorher stand
              hier „6 von 84": Der Zähler kam aus allen Aufgaben inklusive
              erledigter, der Nenner nur aus den offenen — zwei
              Grundgesamtheiten in einer Angabe.
            */
            anzahl={
              zahlenBekannt ? `${Math.min(zuletzt.length, 6)} von ${zuletzt.length}` : undefined
            }
            aktion={
              <Link
                to="/aufgaben"
                search={{ sortierung: "geaendert" }}
                className={verweisKlassen()}
              >
                Alle Aufgaben
              </Link>
            }
          >
            Zuletzt bewegt
          </Rubrik>

          {aufgaben.isError ? null : !zahlenBekannt ? (
            <div className="pt-4">
              <Mikrolabel>Wird geladen …</Mikrolabel>
            </div>
          ) : zuletzt.length === 0 ? (
            <div className="pt-4">
              <Mikrolabel>Noch keine Aufgaben in deinen Projekten.</Mikrolabel>
            </div>
          ) : (
            <ul role="list" aria-labelledby="rubrik-zuletzt">
              {zuletzt.slice(0, 6).map((a) => (
                <AufgabenZeile
                  key={a.id}
                  aufgabe={a}
                  kuerzel={kuerzelZu.get(a.projektId)}
                  zeitspalte="bewegt"
                />
              ))}
            </ul>
          )}
        </div>

        {/*
          Flex-Spalte mit `gap` statt `mt-11` am zweiten Kind.

          `MetarrKarte` gibt `null` zurück, wenn nichts zur Entscheidung
          liegt. Der feste Abstand blieb dann trotzdem stehen, und die rechte
          Rubrikenlinie saß 44px unter der linken. Ein Kind, das nicht
          gerendert wird, erzeugt in einer Flex-Spalte auch keine Lücke.
        */}
        <div className="flex min-w-0 max-w-[344px] flex-1 basis-[300px] flex-col gap-11">
          <MetarrKarte />

          <div>
          <Rubrik
            id="rubrik-projekte"
            anzahl={liste.length > 6 ? `6 von ${liste.length}` : undefined}
            aktion={
              auswahl === ALLE_PROJEKTE ? (
                <Link to="/projekte" className={verweisKlassen()}>
                  Alle Projekte
                </Link>
              ) : null
            }
          >
            {auswahl === ALLE_PROJEKTE ? "Projekte" : "Ausgewähltes Projekt"}
          </Rubrik>
          <ul role="list" aria-labelledby="rubrik-projekte">
            {projekteGeordnet.slice(0, 6).map((p) => (
              <li key={p.id}>
                <Link
                  to="/projekte/$id"
                  params={{ id: p.id }}
                  className={zeileKlassen({ hoehe: 44, klickbar: true })}
                >
                  <Kennung breite={34}>{p.kuerzel}</Kennung>
                  <Zeilentitel titel={p.name} leise>
                    {p.name}
                  </Zeilentitel>
                  <Mikrolabel grad={11} zahl className="flex-none">
                    {p.offen}
                  </Mikrolabel>
                </Link>
              </li>
            ))}
          </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
